import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function VisitDetailsPage() {
    const { serverId, visitId } = useParams();
    const navigate = useNavigate();
    const [visit, setVisit] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchVisit() {
            try {
                // Path: servers -> {serverId} -> allVisits -> {visitId}
                const docRef = doc(db, 'servers', serverId, 'allVisits', visitId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setVisit({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.error("No such visit!");
                }
            } catch (error) {
                console.error("Error fetching visit:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchVisit();
    }, [serverId, visitId]);

    if (loading) return <div style={{ padding: 20 }}>Loading Visit Details...</div>;
    if (!visit) return <div style={{ padding: 20 }}>Visit not found.</div>;

    // Helper Styles
    const cardStyle = {
        backgroundColor: 'white', borderRadius: '8px', padding: '25px', marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #eee'
    };
    const headerStyle = { margin: '0 0 15px', color: '#2c3e50', fontSize: '16px', borderBottom: '2px solid #f7fafc', paddingBottom: '10px' };
    const labelStyle = { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#888', marginBottom: '4px', fontWeight: 'bold' };
    const valueStyle = { fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '15px' };

    // Geolocation Helper
    const openMap = () => {
        // Your JSON has "taskLocation" as a geopoint object [lat, long] or {latitude, longitude}
        // Firestore GeoPoints usually come as object { _lat: ..., _long: ... } or { latitude: ..., longitude: ... }
        // We handle different structures safely:
        const lat = visit.taskLocation?._lat || visit.taskLocation?.latitude || visit.taskLocation?.[0];
        const lng = visit.taskLocation?._long || visit.taskLocation?.longitude || visit.taskLocation?.[1];

        if (lat && lng) {
            window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
        } else {
            alert("No geolocation data available for this visit.");
        }
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            
            {/* Page Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <h1 style={{ margin: 0, fontSize: '24px', color: '#2c3e50' }}>Visit Details</h1>
                        <span style={{ 
                            padding: '4px 12px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase',
                            backgroundColor: visit.status === 'missed' ? '#fff5f5' : '#e6fffa',
                            color: visit.status === 'missed' ? '#c53030' : '#2c7a7b',
                            border: `1px solid ${visit.status === 'missed' ? '#feb2b2' : '#b2f5ea'}`
                        }}>
                            {visit.status}
                        </span>
                    </div>
                    <p style={{ margin: '5px 0 0', color: '#666' }}>ID: {visit.id}</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                
                {/* 1. Overview Card */}
                <div style={cardStyle}>
                    <h3 style={headerStyle}>📅 Overview</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <span style={labelStyle}>Date</span>
                            <div style={valueStyle}>{visit.date}</div>
                        </div>
                        <div>
                            <span style={labelStyle}>Time Range</span>
                            <div style={valueStyle}>{visit.timeRange?.from} - {visit.timeRange?.to}</div>
                        </div>
                    </div>
                    
                    <span style={labelStyle}>Service User</span>
                    <div style={valueStyle}>{visit.service_user_name || visit.username || visit.userID}</div>

                    <span style={labelStyle}>Address</span>
                    <div style={valueStyle}>
                        {visit.address?.firstLineOfAddress}<br />
                        {visit.address?.town}, {visit.address?.postalCode}
                    </div>
                </div>

                {/* 2. Tasks Card */}
                <div style={cardStyle}>
                    <h3 style={headerStyle}>✅ Tasks & Activities</h3>
                    
                    <span style={labelStyle}>Priority Tasks</span>
                    {visit.priorityTasks && visit.priorityTasks.length > 0 ? (
                        <ul style={{ paddingLeft: '20px', marginTop: '5px', marginBottom: '15px' }}>
                            {visit.priorityTasks.map((task, i) => <li key={i} style={{ color: '#c53030', fontWeight: 'bold' }}>{task}</li>)}
                        </ul>
                    ) : <div style={{...valueStyle, color: '#999'}}>No priority tasks.</div>}

                    <span style={labelStyle}>General Activities</span>
                    {visit.assignedActivities && visit.assignedActivities.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {visit.assignedActivities.map((act, i) => (
                                <span key={i} style={{ padding: '4px 10px', background: '#edf2f7', borderRadius: '4px', fontSize: '13px' }}>{act}</span>
                            ))}
                        </div>
                    ) : <div style={{...valueStyle, color: '#999'}}>No activities assigned.</div>}
                    
                    <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                        <span style={labelStyle}>Progress</span>
                        <div style={valueStyle}>{visit.taskCompleted || 0} / {visit.totalTasks || 0} Tasks Completed</div>
                    </div>
                </div>

                {/* 3. Medical Card */}
                <div style={cardStyle}>
                    <h3 style={headerStyle}>💊 Medication</h3>
                    {visit.medications && visit.medications.length > 0 ? (
                        <ul>
                            {visit.medications.map((med, i) => (
                                <li key={i} style={{ marginBottom: '5px' }}>{med}</li>
                            ))}
                        </ul>
                    ) : (
                        <div style={{ color: '#666', fontStyle: 'italic' }}>No medications scheduled for this visit.</div>
                    )}
                </div>

                {/* 4. Staff & Logs Card */}
                <div style={cardStyle}>
                    <h3 style={headerStyle}>👥 Staff & Logistics</h3>
                    
                    <span style={labelStyle}>Assigned Staff IDs</span>
                    <div style={valueStyle}>{visit.staffIDs?.join(', ') || 'Unassigned'}</div>

                    <span style={labelStyle}>Logs</span>
                    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                        <div><strong>Created:</strong> {visit.createdAt?.toDate ? new Date(visit.createdAt.toDate()).toLocaleString() : '-'}</div>
                        <div><strong>Last Modified:</strong> {visit.lastModified?.toDate ? new Date(visit.lastModified.toDate()).toLocaleString() : '-'}</div>
                        <div><strong>Check-in Loc:</strong> {visit.taskLocation ? 'recorded' : 'not recorded'}</div>
                    </div>

                    <button 
                        onClick={openMap}
                        style={{ 
                            marginTop: '15px', width: '100%', padding: '10px', 
                            backgroundColor: '#4299e1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' 
                        }}
                    >
                        📍 View Check-in Location on Maps
                    </button>
                </div>

            </div>
        </div>
    );
}